import axios from 'axios'
import getHeaders from '../helpers/headers'
import config from '../../constants/endpoints.js'

const {
  endpoints: {
    conversation: {
      messages,
      subjects,
      notes,
      deferred,
      getChannels,
      sendSmsMessage,
      getUsers,
      sendMsg,
      drafts,
      draftById,
      state,
      signature,
      msgSync: msgSyncURL,
      read,
      addReactions,
      removeReactions,
      redirectUrl
    }
  }
} = config

/**
 * Send a message to a specific user from dashboard.
 *
 * @param {String} data.userId External id of the user.
 * @param {String} data.dashuserId Internal id of the operator.
 * @param {String} data.locale Locale of the operator.
 * @param {String} data.message Message text to send.
 * @param {Array<Object>} data.attachments Messge attachments.
 */
const sendMessage = (data) => axios.post(messages, data, { headers: getHeaders(true) })

const sendDeferredMessage = (data) => axios.post(deferred, data, { headers: getHeaders(true) })

/**
 * Send a note to a specific user chat.
 *
 * @param {String} data.userId External id of the user.
 * @param {String} data.dashuserId Internal id of the operator.
 * @param {String} data.locale Locale of the operator.
 * @param {String} data.message Message text to send.
 * @param {Array<Object>} data.attachments Messge attachments.
 */
const sendNote = (data) => axios.post(notes, data, { headers: getHeaders(true) })

/**
 * Get messages from a user conversation.
 *
 * @param {String} params.userId External user id.
 * @param {Date} params.lastMessageDate Date of the last message creation.
 * @param {Boolean} params.isPoll Is from polling or load more action.
 */
const getMessages = (params) => axios.get(messages, { headers: getHeaders(true), params })

/**
 * Get subjects from a user conversation.
 *
 * @param {String} userId User id.
 * @param {Object} params User params.
 */
const getSubjects = ({ userId, params }) => axios.get(subjects.replace(':userId', userId), { params, headers: getHeaders(true) })

const getDeferredMessages = (params) => axios.get(deferred, { headers: getHeaders(true), params })

const deleteDeferred = (deferredId) => axios.delete(`${deferred}/${deferredId}`, { headers: getHeaders(true) })

/**
 * Get filtered messages from user.
 *
 * @param {String} params.userId External user id.
 * @param {String} params.filterTypes Comma separated string of filters.
 * @param {String} params.text Text query.
 */
const getFilteredMessages = (params) => axios.get(messages, { headers: getHeaders(true), params })

/**
 * Get channels opt-out
 *
 * @param {String} params.type `whatsapp`, `sms` o `chatmail`
 */
const getChannelsOPTOUT = (params) => axios.get(getChannels.replace(':type', params.type), { headers: getHeaders(true) })

/**
 * Send SMS Message
 *
 * @param {Object} data {channelId, userId, phone, message: AutomatedMessage}
 */
const sendSMS = (data) => axios.post(sendSmsMessage, data, { headers: getHeaders(true) })

/**
 * Get users opt-out
 *
 * @param {String} params.channel 'whatsapp', 'sms' or 'chatmail'
 * @param {String} params.text Text query
 */
const getUsersOPTOUT = (params) => axios.get(getUsers, { params, headers: getHeaders(true) })

/**
 * Send message opt-out
 *
 * @param {Object} data Message data
 */
const sendMessageOPTOUT = (data) => axios.post(sendMsg, data, { headers: getHeaders(true) })

/**
 * Create conversation draft
 *
 * @param {Object} data Draft message data
 */
const createDraft = (data) => axios.post(drafts, data, { headers: getHeaders(true) })

/**
 * Get all conversation drafts
 *
 * @param {String} params.userId Conversation user id
 */
const getDrafts = (params) => axios.get(drafts, { params, headers: getHeaders(true) })

/**
 * Get conversation draft
 *
 * @param {String} draftId Draft id
 */
const getDraftById = ({ draftId }) => axios.get(draftById.replace(':id', draftId), { headers: getHeaders(true) })

/**
 * Update conversation draft
 *
 * @param {String} draftId Draft id
 * @param {Object} data Draft data
 */
const updateDraft = ({ draftId, data }) => axios.put(draftById.replace(':id', draftId), data, { headers: getHeaders(true) })

/**
 * Delete conversation draft
 *
 * @param {String} draftId Draft id
 */
const deleteDraft = ({ draftId }) => axios.delete(draftById.replace(':id', draftId), { headers: getHeaders(true) })

/**
 * Change user state
 *
 * @param {String} userId User id
 * @param {String} state State
 * @param {String} subjectId Subject Id
 */
const changeState = (data) => axios.post(state, data, { headers: getHeaders(true) })

/**
 * Get Signature
 *
 * @param {String} id Signature id
 */
const getSignature = (id) => axios.get(signature + '/' + id, { headers: getHeaders(true) })

/**
 * Messages sync
 *
 */
const msgSync = (userId) => axios.post(msgSyncURL, { userId }, { headers: getHeaders(true) })

/**
 * Messages read
 *
 */
const msgRead = (data) => axios.post(read, data, { headers: getHeaders(true) })

/**
 * Reaction add
 * @param {Object} data
 * @param {String} data.emoji
 * @param {String} data.messageId
 * @param {String} data.userId
 */
const addReaction = (data) => axios.post(addReactions, data, { headers: getHeaders(true) })

/**
 * Reaction remove
 * @param {Object} data
 * @param {String} data.messageId
 * @param {String} data.userId
 */
const removeReaction = (data) => axios.post(removeReactions, data, { headers: getHeaders(true) })

/*
 * Get Redirect Url
 *
 * @param {Object} data Just an userId
 * @param {String} data.userId User id
 *
 */
const getRedirectUrl = (data) => axios.post(redirectUrl, data, { headers: getHeaders(true) })

export default {
  sendMessage,
  sendDeferredMessage,
  sendNote,
  getMessages,
  getSubjects,
  getFilteredMessages,
  getDeferredMessages,
  deleteDeferred,
  getChannelsOPTOUT,
  sendSMS,
  getUsersOPTOUT,
  sendMessageOPTOUT,
  createDraft,
  getDrafts,
  getDraftById,
  updateDraft,
  deleteDraft,
  changeState,
  getSignature,
  msgSync,
  msgRead,
  addReaction,
  removeReaction,
  getRedirectUrl
}
